import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import { CheckCircleOutline, ErrorOutline, WarningAmber } from "@mui/icons-material";

export type TomIndicador = "neutro" | "bom" | "atencao" | "critico";

export interface Indicador {
  titulo: string;
  valor: React.ReactNode;
  // Sempre presente e numa linha só, pra todos os cards terem o mesmo desenho.
  nota: string;
  tom?: TomIndicador;
}

const COR_TOM = { bom: "success", atencao: "warning", critico: "error" } as const;
const ICONE_TOM = { bom: CheckCircleOutline, atencao: WarningAmber, critico: ErrorOutline } as const;

// Faixa de indicadores no topo de uma tela; o estado vem com ícone, nunca só pela cor.
export function Indicadores({ itens }: { itens: Indicador[] }) {
  const theme = useTheme();
  return (
    <Box
      role="list"
      aria-label="Resumo"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: `repeat(${itens.length}, minmax(0, 1fr))` },
        gap: 1.5,
        mb: 2,
      }}
    >
      {itens.map((item) => {
        const tom = item.tom ?? "neutro";
        const cor = tom === "neutro" ? undefined : theme.palette[COR_TOM[tom]].main;
        const Icone = tom === "neutro" ? null : ICONE_TOM[tom];
        return (
          <Box
            role="listitem"
            key={item.titulo}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: cor ? alpha(cor, 0.5) : "divider",
              bgcolor: cor ? alpha(cor, 0.06) : "background.paper",
              minWidth: 0,
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minHeight: 20 }}>
              <Typography variant="body2" color="text.secondary" noWrap title={item.titulo} sx={{ flex: 1 }}>
                {item.titulo}
              </Typography>
              {Icone && <Icone sx={{ fontSize: 18, color: cor }} />}
            </Stack>
            <Typography variant="h5" noWrap sx={{ fontWeight: 800, my: 0.5 }}>
              {item.valor}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" noWrap title={item.nota}>
              {item.nota}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
